import React, { useState, useEffect } from 'react';

import { Field, Fields, Input, SectionTitle, Select } from '../../design-system/components/desktop';
import PlacesInput from '../UI/GooglePlacesAutocomplete/PlacesInput';
import { PROVINCES, CITIES_BY_PROVINCE } from './utils/constants';
import SavedAddressSelector from './SavedAddressSelector';

/**
 * The delivery address, on the shipping path only.
 *
 * Every behaviour is preserved: saved-address selection populates the form and
 * clears the "missing" flags, manually editing line 1 or the city drops the
 * saved-address selection, and a Google Places pick fills whatever the API
 * returned and reveals a manual field for each part it did NOT return. That
 * last rule is the point of `missingFields` and it is easy to mistake for dead
 * code, so: the province, city, postal code and country inputs appear only when
 * Google could not supply them.
 *
 * The address control is `PlacesInput`, the shared track-aware wrapper around
 * `GooglePlacesAutocomplete`. The underlying component wraps
 * `react-google-autocomplete` and the library has no equivalent, so it is
 * handed the track's own input class rather than being re-implemented. It is
 * shared with five other routes, so its `.places-input` wrapper class is not
 * this batch's to delete.
 *
 * The stray `import { CloudHail } from 'lucide-react'` that used to sit at the
 * top of this file, unused, is gone with the rest of the icon set.
 */
const BillingForm = ({ form, handleChange, errors = {} }) => {
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);
  // Which fields Google did NOT fill, and therefore have to be asked for.
  const [missingFields, setMissingFields] = useState({
    state: false,
    city: false,
    postal_code: false,
    country: false,
  });

  const province = PROVINCES.find(
    (p) =>
      p.toLowerCase() ===
      String(form.state || '')
        .trim()
        .toLowerCase()
  );

  const cities = province ? CITIES_BY_PROVINCE[province] || [] : [];

  const handleSavedAddressSelect = (address) => {
    if (!address) {
      setSelectedSavedAddress(null);
      return;
    }

    setSelectedSavedAddress(address);

    handleChange('billing_address_line1')({ target: { value: address.address_line1 || '' } });
    handleChange('billing_address_line2')({ target: { value: address.address_line2 || '' } });
    handleChange('city')({ target: { value: address.city || '' } });
    handleChange('state')({ target: { value: address.province || '' } });
    handleChange('postal_code')({ target: { value: address.postal_code || '' } });

    setMissingFields({ state: false, city: false, postal_code: false, country: false });
  };

  // A manual edit means the buyer is no longer using the saved address.
  useEffect(() => {
    if (selectedSavedAddress) {
      const isManuallyChanged =
        form.billing_address_line1 !== selectedSavedAddress.address_line1 ||
        form.city !== selectedSavedAddress.city;

      if (isManuallyChanged) {
        setSelectedSavedAddress(null);
      }
    }
  }, [form.billing_address_line1, form.city, selectedSavedAddress]);

  const handlePlaceSelect = (placeDetails) => {
    setSelectedSavedAddress(null);
    if (!placeDetails || !placeDetails.addressComponents) return;

    const components = placeDetails.addressComponents;
    let postalCode = '';
    let city = '';
    let selectedProvince = '';
    let country = '';

    components.forEach((component) => {
      const types = component.types;

      if (types.includes('postal_code')) {
        postalCode = component.long_name;
      }

      if (types.includes('locality')) {
        city = component.long_name;
      } else if (types.includes('sublocality_level_1') && !city) {
        city = component.long_name;
      }

      if (types.includes('administrative_area_level_1')) {
        selectedProvince = component.long_name;
      }

      if (types.includes('country')) {
        country = component.long_name;
      }
    });

    setMissingFields({
      state: !selectedProvince,
      city: !city,
      postal_code: !postalCode,
      country: !country,
    });

    if (selectedProvince) handleChange('state')({ target: { value: selectedProvince } });
    if (city) handleChange('city')({ target: { value: city } });
    if (postalCode) handleChange('postal_code')({ target: { value: postalCode } });
    if (country) handleChange('country')({ target: { value: country } });
  };

  return (
    <section className="dsa-checkout__section">
      <SectionTitle>Delivery address</SectionTitle>

      <SavedAddressSelector
        onSelectAddress={handleSavedAddressSelect}
        selectedAddressId={selectedSavedAddress?.id}
      />

      <Fields cols={1}>
        <Field label="Address line 1" error={errors.billing_address_line1}>
          <PlacesInput
            error={Boolean(errors.billing_address_line1)}
            value={form.billing_address_line1}
            onChange={handleChange('billing_address_line1')}
            placeholder="14 Bree Street"
            onPlaceSelect={handlePlaceSelect}
          />
        </Field>

        <Field label="Address line 2" optional>
          <PlacesInput
            value={form.billing_address_line2}
            onChange={handleChange('billing_address_line2')}
            placeholder="Unit 302"
            name="billing_address_line2"
          />
        </Field>

        {missingFields.state && (
          <Field label="Province" error={errors.state}>
            <Select
              value={form.state}
              onChange={(value) => handleChange('state')({ target: { value } })}
              placeholder="Select province"
              options={PROVINCES.map((p) => ({ label: p, value: p }))}
            />
          </Field>
        )}

        {missingFields.city && (
          <Field label="City" error={errors.city}>
            <Select
              value={form.city}
              onChange={(value) => handleChange('city')({ target: { value } })}
              placeholder="Select city"
              options={cities.map((c) => ({ label: c, value: c }))}
            />
          </Field>
        )}

        {missingFields.postal_code && (
          <Field label="Postal code" error={errors.postal_code}>
            <Input
              block
              error={Boolean(errors.postal_code)}
              placeholder="8001"
              value={form.postal_code}
              onChange={handleChange('postal_code')}
            />
          </Field>
        )}

        {missingFields.country && (
          <Field label="Country" error={errors.country}>
            <Input
              block
              error={Boolean(errors.country)}
              placeholder="South Africa"
              value={form.country}
              onChange={handleChange('country')}
            />
          </Field>
        )}

        {/* Read only, and every amount on Sleevr is in Rands. */}
        <Field label="Currency" hint="Every amount on Sleevr is in Rands.">
          <Input block readOnly value={form.currency} />
        </Field>
      </Fields>
    </section>
  );
};

export default BillingForm;
